import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ReactComponent as OccasiaLogoIcon } from '../icons/occasia-logo-icon.svg';
import { LoginModal, SignupModal, VenueLoginModal, VenueSignupModal } from '../Auth';
import { useModalState } from '../Header/hooks/useModalState';
import { useClickOutside } from '../Header/hooks/useClickOutside';
import { useCloseOnEscape } from '../../hooks/useCloseOnEscape';
import { useAuth } from '../../contexts/AuthContext';
import HeaderAuth from '../Header/HeaderAuth';
import AskHelpButton from '../Comms/AskHelpButton';
import './Landing2Header.css';

const VARIANT_VENUES = 'venues';
const VARIANT_LIGHT = 'light';

/**
 * Landing2 header - used on Landing2, About, Blog, Privacy, Terms.
 * @param {Object} props
 * @param {'events'|'venues'|'light'} props.variant - 'events' = light bg/peach, 'venues' = dark green, 'light' = white content pages
 * @param {boolean} props.showPersonaSwitcher - Show For event organisers / For venues switcher (Landing2 only)
 * @param {'events'|'venues'} props.persona - Controlled persona (when with showPersonaSwitcher on Landing2)
 * @param {function} props.onPersonaChange - Called when persona changes, (persona) => void
 */
const Landing2Header = ({
  variant = VARIANT_LIGHT,
  showPersonaSwitcher = false,
  persona: controlledPersona,
  onPersonaChange = () => {}
}) => {
  const navigate = useNavigate();
  const [internalPersona, setInternalPersona] = useState('events');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const headerRef = useRef(null);
  const persona = controlledPersona !== undefined ? controlledPersona : internalPersona;
  const lightLinks = variant === VARIANT_VENUES;

  const closeMenu = () => setIsMenuOpen(false);

  const handleSignInClick = () => {
    closeMenu();
    if (persona === 'venues' && showPersonaSwitcher) {
      const hostname = window.location.hostname;
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        navigate('/dashboard-login');
      } else if (hostname.includes('occasia.io')) {
        window.location.href = 'https://www.venues.occasia.io';
      } else {
        navigate('/dashboard-login');
      }
    } else {
      handleLoginClick();
    }
  };

  const {
    showLoginModal,
    showSignupModal,
    showVenueLoginModal,
    showVenueSignupModal,
    handleLoginClick,
    closeLoginModal,
    closeSignupModal,
    closeVenueLoginModal,
    closeVenueSignupModal,
    switchToVenueLogin,
    switchToVenueSignup,
    switchToSignup,
    switchToLogin
  } = useModalState();

  const handlePersonaChange = (newPersona) => {
    if (controlledPersona === undefined) setInternalPersona(newPersona);
    onPersonaChange(newPersona);
  };

  const goToEvents = () => {
    closeMenu();
    if (showPersonaSwitcher) {
      handlePersonaChange('events');
      return;
    }
    navigate('/');
  };

  const goToVenues = () => {
    closeMenu();
    if (showPersonaSwitcher) {
      handlePersonaChange('venues');
      return;
    }
    navigate('/onboard?persona=venues');
  };

  const linkClass = lightLinks ? 'l2-header-link--light' : '';
  const logoClass = lightLinks ? 'l2-header-logo--light' : '';
  const switcherClass = lightLinks ? 'l2-header-persona-switcher--dark' : '';

  const { currentUser, isLoggingOut } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useCloseOnEscape(isMenuOpen, closeMenu);
  useClickOutside(headerRef, () => {
    if (isMenuOpen) closeMenu();
  });

  useEffect(() => {
    const header = headerRef.current;
    const nav = header?.querySelector('.l2-header-nav');
    if (!header || !nav) return undefined;
    const apply = () => {
      const height = `${nav.offsetHeight}px`;
      header.style.setProperty('--l2-header-height', height);
      header.closest('.page-full, .l2-page')?.style.setProperty('--l2-header-height', height);
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(nav);
    return () => observer.disconnect();
  }, [showPersonaSwitcher, isMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isMenuOpen]);

  return (
    <>
      <header
        ref={headerRef}
        className={`l2-header-bar ${isScrolled ? 'l2-header-bar--scrolled' : ''} ${isMenuOpen ? 'l2-header-bar--menu-open' : ''}`}
      >
      <nav className="l2-header-nav">
        <Link to="/" className={`l2-header-logo ${logoClass}`} aria-label="Occasia home" onClick={closeMenu}>
          <OccasiaLogoIcon className="l2-header-logo-svg" aria-label="Occasia" />
        </Link>

        {showPersonaSwitcher && (
          <div className={`l2-header-persona-switcher ${switcherClass}`}>
            <button
              type="button"
              className={`l2-header-persona-btn ${persona === 'events' ? 'l2-header-persona-btn--active' : ''}`}
              onClick={() => handlePersonaChange('events')}
            >
              For event organisers
            </button>
            <button
              type="button"
              className={`l2-header-persona-btn ${persona === 'venues' ? 'l2-header-persona-btn--active' : ''}`}
              onClick={() => handlePersonaChange('venues')}
            >
              For venues
            </button>
          </div>
        )}

        <div className="l2-header-links">
          <Link to="/about" className={`l2-header-link ${linkClass}`}>About</Link>
          <Link to="/blog" className={`l2-header-link ${linkClass}`}>Journal</Link>
          <div className={`l2-header-auth-wrapper ${lightLinks ? 'l2-header-auth-wrapper--light' : ''}`}>
            <HeaderAuth
              isLandingPage={lightLinks}
              currentUser={currentUser}
              onLogout={() => {}}
              isLoggingOut={isLoggingOut}
              onLoginClick={handleSignInClick}
            />
          </div>
        </div>

        <div className="l2-header-tools">
          <AskHelpButton onDark={lightLinks} />
          <button
            type="button"
            className={`l2-header-menu-btn ${isMenuOpen ? 'is-open' : ''} ${lightLinks ? 'l2-header-menu-btn--light' : ''}`}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen((open) => !open)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </nav>

      <div
        className={`l2-header-menu ${isMenuOpen ? 'is-open' : ''}`}
        id="l2-header-menu"
        onClick={closeMenu}
      >
        <div className="l2-header-menu-panel" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="l2-header-menu-link" onClick={goToEvents}>
          For event organisers
        </button>
        <button type="button" className="l2-header-menu-link" onClick={goToVenues}>
          For venues
        </button>
        <Link to="/about" className="l2-header-menu-link" onClick={closeMenu}>
          About
        </Link>
        <Link to="/blog" className="l2-header-menu-link" onClick={closeMenu}>
          Journal
        </Link>
        {currentUser ? (
          <div className="l2-header-menu-auth">
            <HeaderAuth
              isLandingPage={false}
              currentUser={currentUser}
              onLogout={() => {}}
              isLoggingOut={isLoggingOut}
              onLoginClick={handleSignInClick}
            />
          </div>
        ) : (
          <button type="button" className="l2-header-menu-link" onClick={handleSignInClick}>
            Sign in
          </button>
        )}
        </div>
      </div>
      </header>

      <LoginModal
        isOpen={showLoginModal}
        onClose={closeLoginModal}
        onSwitchToSignup={switchToSignup}
      />
      <SignupModal
        isOpen={showSignupModal}
        onClose={closeSignupModal}
        onSwitchToLogin={switchToLogin}
      />
      <VenueLoginModal
        isOpen={showVenueLoginModal}
        onClose={closeVenueLoginModal}
        onSwitchToSignup={switchToVenueSignup}
      />
      <VenueSignupModal
        isOpen={showVenueSignupModal}
        onClose={closeVenueSignupModal}
        onSwitchToLogin={switchToVenueLogin}
      />
    </>
  );
};

export default Landing2Header;
